<script lang="ts" setup>
import type { FaqItem } from '#forgd/app/utils/faqs'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@forgd/ui-kit/accordion'

type Layout = 'compact' | 'split' | 'plain'

const props = withDefaults(defineProps<{
  faqs: FaqItem[]
  layout?: Layout
  itemClass?: string
}>(), { layout: 'compact', itemClass: '' })

const numberClass = computed(() =>
  props.layout === 'split' ? 'w-[17px] shrink-0 lg:w-1/2' : 'w-[17px] shrink-0',
)
</script>

<template>
  <!-- Collapsed answers stay in the DOM so they're in the server-rendered HTML. -->
  <Accordion type="single" collapsible default-value="1" :unmount-on-hide="false" as-child>
    <div class="flex flex-col">
      <AccordionItem
        v-for="(faq, index) in faqs"
        :key="index"
        :value="`${index + 1}`"
        class="border-b border-default last:border-b-0"
      >
        <div class="flex items-start gap-6 py-5" :class="itemClass">
          <span v-if="layout !== 'plain'" class="font-mono text-sm font-medium text-primal" :class="numberClass">
            {{ `${index + 1}`.padStart(2, '0') }}
          </span>
          <div class="flex flex-1 flex-col gap-5">
            <AccordionTrigger class="p-0 text-md font-semibold md:text-sm">
              {{ faq.question }}
            </AccordionTrigger>
            <AccordionContent class="p-0">
              <div class="flex flex-col gap-5">
                <div class="flex max-w-125 flex-col gap-4 text-sm text-secondary">
                  <template v-for="(block, i) in faq.answer" :key="i">
                    <blockquote
                      v-if="typeof block === 'object'"
                      class="border-l-2 border-accent py-1 pl-6 text-sm leading-relaxed text-secondary"
                    >
                      {{ block.quote }}
                    </blockquote>
                    <p v-else>
                      {{ block }}
                    </p>
                  </template>
                  <ul v-if="faq.bullets" class="flex list-disc flex-col gap-2 pl-5">
                    <li v-for="(bullet, i) in faq.bullets" :key="i">
                      {{ bullet }}
                    </li>
                  </ul>
                </div>
                <img
                  v-if="faq.image"
                  :src="faq.image.src"
                  :alt="faq.image.alt"
                  loading="lazy"
                  class="w-full max-w-151.5 rounded-lg"
                >
              </div>
            </AccordionContent>
          </div>
        </div>
      </AccordionItem>
    </div>
  </Accordion>
</template>
